import { useCallback, useEffect, useRef, useState } from 'react';

import { updateProjectContent } from '@/lib/api-client';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export function useAutoSave(options: {
  projectId: string;
  rawContent: string;
  debounceMs?: number;
  onSaveError?: (error: unknown) => void;
}): { saveStatus: SaveStatus; saveNow: () => void } {
  const { projectId, rawContent, debounceMs = 2000, onSaveError } = options;
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const isFirstRender = useRef(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const onSaveErrorRef = useRef(onSaveError);
  onSaveErrorRef.current = onSaveError;

  const save = useCallback(
    async (content: string) => {
      setSaveStatus('saving');
      try {
        await updateProjectContent(projectId, content);
        setSaveStatus('saved');
      } catch (err) {
        setSaveStatus('error');
        onSaveErrorRef.current?.(err);
      }
    },
    [projectId],
  );

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      save(rawContent);
    }, debounceMs);

    return () => clearTimeout(timerRef.current);
  }, [rawContent, debounceMs, save]);

  const saveNow = useCallback(() => {
    clearTimeout(timerRef.current);
    save(rawContent);
  }, [save, rawContent]);

  return { saveStatus, saveNow };
}
